import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { FiSearch } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import "./StudentFees.css";

const StudentFees = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false); // Changed to false since we're using mock data
  const [searchOpen, setSearchOpen] = useState({});
  const [searchTerms, setSearchTerms] = useState({});

  useEffect(() => {
    // Mock classes data
    const mockClasses = [
      { id: 1, class_name: "Nursery" },
      { id: 2, class_name: "LKG" },
      { id: 3, class_name: "UKG" },
      { id: 4, class_name: "Class 1" },
      { id: 5, class_name: "Class 2" },
    ];

    // Mock students data with fees
    const mockStudents = [
      {
        studentId: "ST001",
        firstName: "Rahul",
        lastName: "Sharma",
        className: "Nursery",
        monthlyFee: 1200,
      },
      {
        studentId: "ST002",
        firstName: "Priya",
        lastName: "Patel",
        className: "Nursery",
        monthlyFee: 1200,
      },
      {
        studentId: "ST003",
        firstName: "Aarav",
        lastName: "Singh",
        className: "LKG",
        monthlyFee: 1500,
      },
      {
        studentId: "ST004",
        firstName: "Ananya",
        lastName: "Gupta",
        className: "UKG",
        monthlyFee: 1800,
      },
      {
        studentId: "ST005",
        firstName: "Vihaan",
        lastName: "Joshi",
        className: "Class 1",
        monthlyFee: 2000,
      },
      {
        studentId: "ST006",
        firstName: "Ishaan",
        lastName: "Malhotra",
        className: "Class 2",
        monthlyFee: 2200,
      },
    ];

    setClasses(mockClasses);
    setStudents(mockStudents);
  }, []);

  const handleFeeChange = (studentId, value) => {
    // Update local state for immediate UI feedback
    setStudents(
      students.map((student) => {
        if (student.studentId === studentId) {
          return { ...student, monthlyFee: value };
        }
        return student;
      })
    );

    // Mock success notification
    toast.success("Student fee updated successfully");
  };

  const groupStudentsByClass = () => {
    const grouped = {};
    classes.forEach((classItem) => {
      const classStudents = students.filter(
        (student) => student.className === classItem.class_name
      );

      // Apply search filter if search is active for this class
      if (
        searchOpen[classItem.class_name] &&
        searchTerms[classItem.class_name]
      ) {
        const searchTerm = searchTerms[classItem.class_name].toLowerCase();
        grouped[classItem.class_name] = classStudents.filter(
          (student) =>
            student.firstName.toLowerCase().includes(searchTerm) ||
            student.lastName.toLowerCase().includes(searchTerm) ||
            student.studentId.toLowerCase().includes(searchTerm)
        );
      } else {
        grouped[classItem.class_name] = classStudents;
      }
    });
    return grouped;
  };

  const studentsByClass = groupStudentsByClass();

  const toggleSearch = (className) => {
    setSearchOpen((prev) => ({
      ...prev,
      [className]: !prev[className],
    }));

    // Clear search term when closing search
    if (searchOpen[className]) {
      setSearchTerms((prev) => ({
        ...prev,
        [className]: "",
      }));
    }
  };

  const handleSearchChange = (className, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [className]: value,
    }));
  };

  return (
    <div className="student-fees-page box fix-height">
      <h2 className="main-heading">Student Monthly Fees</h2>
      <p className="description">
        Set individual monthly fees for each student. These values will be used
        as defaults when generating fee bills.
      </p>

      <div className="student-fees-container">
        {classes.map((classItem) => (
          <div key={classItem.id} className="class-section box">
            <div className="class-header">
              <div className="class-header-main">
                <h3 className="class-title">{classItem.class_name}</h3>
                <div className="class-header-actions">
                  <div className="student-count">
                    {studentsByClass[classItem.class_name]?.length || 0}{" "}
                    Students
                  </div>
                  <button
                    className={`search-toggle ${
                      searchOpen[classItem.class_name] ? "active" : ""
                    }`}
                    onClick={() => toggleSearch(classItem.class_name)}
                    title={
                      searchOpen[classItem.class_name]
                        ? "Close search"
                        : "Search students"
                    }
                  >
                    <FiSearch />
                  </button>
                </div>
              </div>
              {searchOpen[classItem.class_name] && (
                <div className="search-container">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search by name or ID..."
                    value={searchTerms[classItem.class_name] || ""}
                    onChange={(e) =>
                      handleSearchChange(classItem.class_name, e.target.value)
                    }
                  />
                </div>
              )}
            </div>

            {studentsByClass[classItem.class_name]?.length > 0 ? (
              <ul className="student-items">
                {studentsByClass[classItem.class_name].map((student) => (
                  <li key={student.studentId} className="student-item">
                    <div className="student-info">
                      <div className="student-name">
                        {student.firstName} {student.lastName}
                      </div>
                      <div className="student-id">{student.studentId}</div>
                    </div>
                    <div className="student-fee-input">
                      <label htmlFor={`fee-${student.studentId}`}>
                        Monthly Fee:
                      </label>
                      <input
                        id={`fee-${student.studentId}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={student.monthlyFee}
                        onChange={(e) => {
                          // Update local state for immediate feedback
                          setStudents(
                            students.map((s) => {
                              if (s.studentId === student.studentId) {
                                return {
                                  ...s,
                                  monthlyFee: parseFloat(e.target.value) || 0,
                                };
                              }
                              return s;
                            })
                          );
                        }}
                        onBlur={(e) =>
                          handleFeeChange(
                            student.studentId,
                            parseFloat(e.target.value) || 0
                          )
                        }
                      />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="empty-class">
                <div className="empty-icon">👥</div>
                <div className="empty-text">No Students in this class</div>
                <div className="empty-subtext">
                  Students will appear here when enrolled
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default StudentFees;
